// RunMap — the Runs page's map of how the day's (or week's) runs link
// (linked runs phase 7; T9). Bulk sorts on the left, packing lines on the
// right, a line between each pair with the crates and kg of bulk-sort pallets
// tipped across it. Tap a run, on the drawing or in the list under it, to open
// it. On a phone the drawing keeps its size and scrolls sideways; the list is
// the phone's way in. Shown only where linked runs are on.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Network } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import { readState } from '@/lib/readState'
import { fmtKg as kg } from '@/lib/runsKpis'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useLinkedRunsOn } from '@/hooks/useLinkedRuns'
import { stationDayAt } from '@/modules/packhouse/piecework/stationDay'
import { fmtDateZA } from '@/lib/dates'
import ViewChipRow from '@/components/ViewChipRow'
import { buildRunMap, mapWindow, type MapEdgeRow, type MapIntakeRow, type RunMap as Map_ } from '@/lib/runMap'

type Page<T> = PromiseLike<{ data: T[] | null; error: { message: string } | null }>
type Span = 'day' | 'week'

function useRunMap(day: string, span: Span, enabled: boolean) {
  const tenantId = useActiveTenantId()
  const { from, to } = mapWindow(day, span)
  return useQuery({
    queryKey: ['run-map', tenantId, from, to],
    enabled: enabled && !!tenantId,
    queryFn: async () => {
      const [edges, intake] = await Promise.all([
        // Tips of bulk-sort pallets onto lines that are not deleted.
        fetchAllPaged<MapEdgeRow>((lo, hi) => supabase.from('tipping_log')
          .select('tipping_id, run_id, bin_quantity, net_weight_kg, runs!inner(run_number, deleted_at), bins:bin_id(source_run_id, src:source_run_id(run_number))')
          .eq('tenant_id', tenantId!).eq('from_bulk_sort', true).is('runs.deleted_at', null)
          .gte('tipped_at', from).lt('tipped_at', to)
          .order('tipping_id').range(lo, hi) as unknown as Page<MapEdgeRow>),
        // What was tipped onto bulk sorts that are not deleted.
        fetchAllPaged<MapIntakeRow>((lo, hi) => supabase.from('tipping_log')
          .select('tipping_id, run_id, bin_quantity, net_weight_kg, runs!inner(run_number, run_kind, deleted_at)')
          .eq('tenant_id', tenantId!).eq('runs.run_kind', 'BULK_SORT').is('runs.deleted_at', null)
          .gte('tipped_at', from).lt('tipped_at', to)
          .order('tipping_id').range(lo, hi) as unknown as Page<MapIntakeRow>),
      ])
      if (edges.error) throw new Error(edges.error)
      if (intake.error) throw new Error(intake.error)
      return buildRunMap(edges.rows, intake.rows)
    },
  })
}

const ROW = 64, BOX_W = 150, BOX_H = 44, GAP_X = 180
const WIDTH = BOX_W * 2 + GAP_X

function MapBox({ node, x, y, sub, onOpen }: {
  node: { runId: string; runNumber: string; kg: number }; x: number; y: number; sub: string; onOpen: (id: string) => void
}) {
  return (
    <g role="button" tabIndex={0} aria-label={`Open ${node.runNumber}`} className="cursor-pointer"
      onClick={() => onOpen(node.runId)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(node.runId) } }}>
      <rect x={x} y={y} width={BOX_W} height={BOX_H} rx={8} className="fill-surface stroke-line" strokeWidth={1.5} />
      <text x={x + 10} y={y + 18} className="fill-fg" fontSize={13} fontWeight={600}>{node.runNumber}</text>
      <text x={x + 10} y={y + 35} className="fill-fg-secondary" fontSize={11}>{sub} · {kg(node.kg)}</text>
    </g>
  )
}

function MapDrawing({ map, onOpen }: { map: Map_; onOpen: (id: string) => void }) {
  const rows = Math.max(map.bulk.length, map.lines.length, 1)
  const y = (i: number) => i * ROW + (ROW - BOX_H) / 2
  const leftIdx = new Map(map.bulk.map((n, i) => [n.runId, i]))
  const rightIdx = new Map(map.lines.map((n, i) => [n.runId, i]))
  return (
    // Full size on every screen: a phone scrolls it sideways instead of shrinking it.
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${WIDTH} ${rows * ROW}`} width={WIDTH} height={rows * ROW} className="max-w-none"
        role="group" aria-label="How the runs link">
        {map.edges.map(e => {
          const a = leftIdx.get(e.from), b = rightIdx.get(e.to)
          if (a === undefined || b === undefined) return null
          const x1 = BOX_W, y1 = y(a) + BOX_H / 2, x2 = BOX_W + GAP_X, y2 = y(b) + BOX_H / 2
          return (
            <g key={`${e.from}>${e.to}`} aria-hidden>
              <path d={`M${x1},${y1} C${x1 + GAP_X / 2},${y1} ${x2 - GAP_X / 2},${y2} ${x2},${y2}`} className="stroke-accent" strokeWidth={2} fill="none" />
              <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4} textAnchor="middle" className="fill-fg-muted" fontSize={10}>
                {e.count} · {kg(e.kg)}
              </text>
            </g>
          )
        })}
        {map.bulk.map((n, i) => <MapBox key={n.runId} node={n} x={0} y={y(i)} sub="Bulk sort" onOpen={onOpen} />)}
        {map.lines.map((n, i) => <MapBox key={n.runId} node={n} x={BOX_W + GAP_X} y={y(i)} sub="Line" onOpen={onOpen} />)}
      </svg>
    </div>
  )
}

/** The same links in words, each a full-size button: the phone's way in. */
function MapList({ map, onOpen }: { map: Map_; onOpen: (id: string) => void }) {
  const name = (list: Map_['bulk'], id: string) => list.find(x => x.runId === id)?.runNumber ?? id.slice(0, 8)
  return (
    <ul className="space-y-1">
      {map.edges.map(e => (
        <li key={`${e.from}>${e.to}`}>
          <button type="button" onClick={() => onOpen(e.to)}
            className="tap-target-y w-full rounded-lg border border-line px-2 text-left text-caption text-fg-secondary hover:border-line-strong">
            {name(map.bulk, e.from)} → {name(map.lines, e.to)}: {e.count} crates · {kg(e.kg)}
          </button>
        </li>
      ))}
    </ul>
  )
}

function MapResult({ q, day, span, onOpen }: {
  q: ReturnType<typeof useRunMap>; day: string; span: Span; onOpen: (id: string) => void
}) {
  const state = readState(q)
  if (state === 'loading') return <p className="text-xs text-muted">Reading the runs…</p>
  if (state === 'unread' || !q.data) return <p className="text-xs text-muted">Couldn&apos;t read the runs. Check the connection and try again.</p>
  const map = q.data
  if (map.bulk.length === 0 && map.lines.length === 0) {
    return <p className="text-sm text-fg-secondary">No bulk sort tipped anything {span === 'day' ? `on ${fmtDateZA(day)}` : 'that week'}.</p>
  }
  return (
    <>
      <MapDrawing map={map} onOpen={onOpen} />
      <MapList map={map} onOpen={onOpen} />
    </>
  )
}

const SPANS = [{ key: 'day' as const, label: 'Day' }, { key: 'week' as const, label: 'Week' }]

export default function RunMap() {
  const linkedOn = useLinkedRunsOn().data === true
  const tenantId = useActiveTenantId()
  const navigate = useNavigate()
  const [day, setDay] = useState(() => stationDayAt(Date.now()))
  const [span, setSpan] = useState<Span>('day')
  const [open, setOpen] = useState(false)
  const q = useRunMap(day, span, linkedOn && open)
  if (!linkedOn || !tenantId) return null
  return (
    <div className="card space-y-2">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="tap-target-y w-full flex items-center gap-1.5 text-sm font-semibold text-fg">
        <Network size={16} className="text-accent" /> Run map: how the runs link
      </button>
      {open && (
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" aria-label="Day" className="field" value={day} onChange={e => e.target.value && setDay(e.target.value)} />
          <ViewChipRow label="Day or week" options={SPANS} value={span} onChange={setSpan} />
        </div>
      )}
      {open && <MapResult q={q} day={day} span={span} onOpen={id => navigate(`/runs/${id}`)} />}
    </div>
  )
}
